import { Check } from "lucide-react";
import PageHero from "../components/PageHero";
import FinalCTA from "../components/FinalCTA";

const lowRiskPoints = [
  "Focused on routine, well-defined business functions",
  "Stable, recurring business value",
  "Predictable deliverables and implementation paths",
  "Lower likelihood of sudden regulatory or compliance penalties when proper controls are in place",
];

const About = () => (
  <div data-testid="about-page">
    <PageHero
      overline="About"
      title="About Yana Capital Group"
      subtitle="A consulting firm focused on helping organizations strengthen operations, improve governance, and reduce compliance exposure through practical, stability-focused advisory services."
      image="https://images.pexels.com/photos/1313534/pexels-photo-1313534.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
    />

    {/* Intro */}
    <section className="bg-white border-b border-slate-200">
      <div className="mx-auto max-w-4xl px-6 sm:px-8 lg:px-12 py-24 sm:py-28">
        <p className="text-lg sm:text-xl leading-relaxed text-slate-600">
          We support clients who value stable execution, predictable outcomes, and strategic discipline
          over high-risk, unproven approaches. Our work is grounded in routine, well-defined business needs.
        </p>
      </div>
    </section>

    {/* What a Disciplined Approach Means */}
    <section className="bg-background border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-32 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-6">
            Our Definition
          </p>
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-navy">
            What a Disciplined Approach Means
          </h2>
        </div>
        <div className="lg:col-span-7">
          <ul className="space-y-5">
            {lowRiskPoints.map((p) => (
              <li key={p} className="flex items-start gap-4 bg-white border border-slate-200 p-6">
                <Check size={22} className="text-sage mt-0.5 shrink-0" />
                <span className="text-base sm:text-lg text-slate-700">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    {/* Mission */}
    <section className="bg-white border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-32 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-6">Our Focus</p>
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-navy">Our Mission</h2>
        </div>
        <div className="lg:col-span-7">
          <p className="text-base sm:text-lg leading-relaxed text-slate-600">
            We help organizations create stronger foundations through better governance, more resilient
            operations, and more disciplined compliance practices. Our work is intended to reduce
            avoidable losses, improve operational control, and support sustainable growth.
          </p>
        </div>
      </div>
    </section>

    {/* Leadership */}
    <section className="bg-navy">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-32 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-4">
          <div className="aspect-square w-full max-w-xs bg-navy-light border border-slate-700 flex items-center justify-center">
            <span className="font-heading text-6xl font-extrabold text-gold">CW</span>
          </div>
        </div>
        <div className="lg:col-span-8">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-6">Leadership</p>
          <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-white">Cartier Wilson</h2>
          <p className="mt-2 text-sm uppercase tracking-[0.2em] text-slate-400">Founder &amp; Principal</p>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-300 max-w-2xl">
            Cartier Wilson leads Yana Capital Group with a focus on disciplined operations, practical
            compliance support, and long-term business reliability — helping clients build systems that
            stand up to scrutiny and change.
          </p>
        </div>
      </div>
    </section>

    {/* Achievements placeholder */}
    <section className="bg-white border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-6">
          Selected Achievements
        </p>
        <h2 className="text-3xl sm:text-4xl font-medium tracking-tight text-navy max-w-xl">
          Proof Points
        </h2>
        <div className="mt-12 grid gap-px bg-slate-200 sm:grid-cols-2 lg:grid-cols-4 border border-slate-200">
          {[
            "Process Improvements Delivered",
            "Compliance Readiness Outcomes",
            "Governance Frameworks Implemented",
            "Operational Efficiency Gains",
          ].map((t) => (
            <div key={t} className="bg-slate-50 p-10 text-center">
              <span className="font-mono text-3xl font-medium text-slate-300">—</span>
              <p className="mt-4 text-sm text-slate-600">{t}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-slate-400">
          Verified client outcomes and metrics will be published here as engagements are completed.
        </p>
      </div>
    </section>

    <FinalCTA heading="Partner With a Stable Consulting Firm" buttonLabel="Request a Consultation" />
  </div>
);

export default About;
